import { userAPI } from '../../UserAPI';

export interface WorkflowUsage {
    workflowId: string;
    workflowName: string;
    nodeId: string;
    toolId: string;
}

/**
 * Finds all workflows that use a specific tool
 * @param customerId - The customer ID to search workflows for
 * @param toolId - The ID of the tool to find usages for
 * @returns Promise<WorkflowUsage[]> - Array of workflow usages
 */
export async function findWorkflowsUsingTool(customerId: string, toolId: string): Promise<WorkflowUsage[]> {
    try {
        // Get all marketplace offers for the customer
        const marketplaceOffers = await userAPI.getMarketplaceOffers(customerId);
        
        // Filter for workflows (AgenticWorkflow type)
        const workflows = marketplaceOffers.filter((offer: any) => offer._type === 'AgenticWorkflow');
        
        const usages: WorkflowUsage[] = [];
        
        for (const workflow of workflows) {
            if (workflow.graph && workflow.graph.nodes) {
                for (const node of workflow.graph.nodes) {
                    // Check if this node uses the specified tool
                    if (node.data?.toolObject) {
                        const nodeToolId = getToolIdFromToolObject(node.data.toolObject);
                        if (nodeToolId === toolId) {
                            usages.push({
                                workflowId: workflow._id,
                                workflowName: workflow.name || 'Unnamed Workflow',
                                nodeId: node.id,
                                toolId: nodeToolId
                            });
                        }
                    }
                }
            }
        }
        
        return usages;
    } catch (error) {
        console.error('Error finding workflows using tool:', error);
        return [];
    }
}

/**
 * Extracts tool ID from a tool object
 * @param toolObject - The tool object from a workflow node
 * @returns string - The tool ID
 */
function getToolIdFromToolObject(toolObject: any): string {
    if (!toolObject) return '';
    
    // Try to get raw data
    const rawData = toolObject.getRawData ? toolObject.getRawData() : toolObject;
    const marketplaceOffer = rawData._marketplaceOffer || rawData;
    
    return marketplaceOffer._id || marketplaceOffer.id || '';
}

/**
 * Gets the URL to navigate to a specific workflow
 * @param workflowId - The ID of the workflow to navigate to
 * @param nodeId - Optional node ID to highlight in the workflow
 * @returns string - The navigation URL
 */
export function getWorkflowNavigationUrl(workflowId: string, nodeId?: string): string {
    const baseUrl = `/settings?panel=agent&tab=4&key=workflow&agenticWorkflowId=${workflowId}`;
    return nodeId ? `${baseUrl}&nodeId=${nodeId}` : baseUrl;
}